import { ComponentProps, FC, useMemo, useState } from 'react'
import { stencil } from 'react-common'
import { useTranslation } from 'react-i18next'
import { Avatar, BottomSheet, BottomSheetNodeContext, SvgComponent, Header as UiKitHeader } from '@platform/ui'
import i18n from '@/plugins/i18n'
import styles from './UserMenuNarrow.module.scss'

const CHEVRON = <SvgComponent name="ChevronRightRegularStroke" />
const CHECKMARK = <SvgComponent name="CheckRegularStroke" />

const { UserMenuSlotPlugin, UserMenuBottomSheetSlotPlugin, DropdownItemComponent } = stencil
type Locales = keyof typeof stencil.LOCALE_LABELS

type MenuItem = ComponentProps<typeof UiKitHeader.UserContainer.UserDefaultAvatar>['items'][0]

type SelectEvent = {
  key: string
  keyPath: string[]
}

type UserMenuNarrowProps = {
  userName?: string
  menu: MenuItem[]
  currentLocale: Locales
  onSelect: (param: SelectEvent) => void
}
export const UserMenuNarrow: FC<UserMenuNarrowProps> = (props) => {
  const { userName, menu, onSelect, currentLocale } = props
  const { t } = useTranslation()
  const isRTL = i18n.dir() === 'rtl'

  const items = useMemo(() => {
    const languageItems = menu.find((item) => item.key === 'language')?.items || []

    return [
      {
        header: '',
        content: ({ goToNextLevel }: BottomSheetNodeContext) => (
          <div className={styles.menu}>
            {menu.map((menuItem) => {
              const afterChild = !menuItem.items ? null : CHEVRON

              const handleClick = () => {
                if (menuItem.items) {
                  goToNextLevel()
                  return
                }

                onSelect({
                  key: menuItem.key,
                  keyPath: [menuItem.key],
                })
                setIsOpen(false)
              }
              return (
                <button key={menuItem.key} className={styles.button} onClick={handleClick}>
                  <DropdownItemComponent after={afterChild} disabled={menuItem.disabled}>
                    {menuItem.label}
                  </DropdownItemComponent>
                </button>
              )
            })}
          </div>
        ),
      },
      {
        header: t('_pageHeader.userMenu.chooseLanguage'),
        content: () => (
          <div className={styles.menu}>
            {languageItems.map((item) => {
              const afterChild = currentLocale === item.key ? CHECKMARK : null
              const handleClick = () => {
                onSelect({
                  key: item.key,
                  keyPath: ['language'],
                })
                setIsOpen(false)
              }
              return (
                <button key={item.key} className={styles.button} onClick={handleClick}>
                  <DropdownItemComponent after={afterChild} active={currentLocale === item.key}>
                    {item.label}
                  </DropdownItemComponent>
                </button>
              )
            })}
          </div>
        ),
      },
    ]
  }, [currentLocale, menu, onSelect, t])

  const [isOpen, setIsOpen] = useState(false)

  return (
    <>
      <UserMenuSlotPlugin>
        <Avatar onClick={() => setIsOpen(true)} name={userName} size={32} separator={isRTL ? ' ' : ''} />
      </UserMenuSlotPlugin>
      <UserMenuBottomSheetSlotPlugin>
        <div className={styles.bottomSheetWrapper}>
          <BottomSheet isOpen={isOpen} items={items} onClose={() => setIsOpen(false)} />
        </div>
      </UserMenuBottomSheetSlotPlugin>
    </>
  )
}
